import { mutationWithCallback } from '@adamdotai/core/utils'
import { AlertModal, Button, useToast } from '@adamdotai/design-system'
import { useMutation } from '@urql/next'
import { useTranslations } from 'next-intl'
import React from 'react'
import { graphql } from '@/generated/gql'
import { useQueryParams } from '@/navigation'

type DeleteSummaryItemModalProps = {
    id: string
    isOpen: boolean
    onClose: () => void
    handleDeleteSummary: (id: string) => void
}

const DELETE_SUMMARY_BLOCK =
    graphql(`mutation DeleteHighlight($input: DeleteHighlightInput!){
    deleteHighlightV1(input: $input){
      meetingSummary {
        recommendedContentItems {
          id
        }
      }
    }
  }`)

function DeleteSummaryItemModal({
    isOpen,
    onClose = () => {},
    id,
    handleDeleteSummary,
}: DeleteSummaryItemModalProps) {
    const cancelRef = React.useRef<HTMLButtonElement>(null)
    const t = useTranslations()
    const toast = useToast()
    const { meetingId } = useQueryParams()

    const [{ fetching }, deleteSummaryBlock] = useMutation(DELETE_SUMMARY_BLOCK)

    const handleDeleteSummaryItem = () => {
        const deleteSummaryCallback = (res) => {
            if (res.data && !res.error) {
                toast({
                    status: 'success',
                    content: t('toast.summary-item-deleted-successfully'),
                })

                handleDeleteSummary(id)
            }

            onClose()
        }

        mutationWithCallback(
            deleteSummaryBlock,
            {
                id,
                meetingId,
            },
            deleteSummaryCallback,
        )
    }

    return (
        <AlertModal
            isOpen={isOpen}
            onClose={onClose}
            leastDestructiveRef={cancelRef}
        >
            <AlertModal.Content>
                <AlertModal.Close />
                <AlertModal.Header>
                    {t('meetingroom.delete-summary-item')}
                </AlertModal.Header>
                <AlertModal.Body>
                    {t('meetingroom.sure-delete-summary-item')}
                </AlertModal.Body>
                <AlertModal.Footer>
                    <Button
                        size="md"
                        ref={cancelRef}
                        isDisabled={fetching}
                        onClick={onClose}
                        variant="outline"
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        size="md"
                        isLoading={fetching}
                        colorScheme="error"
                        onClick={handleDeleteSummaryItem}
                    >
                        {t('common.delete')}
                    </Button>
                </AlertModal.Footer>
            </AlertModal.Content>
        </AlertModal>
    )
}

export { DeleteSummaryItemModal }
